import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  Book as BookIcon,
  Save as SaveIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import habitService from '../services/habitService';

const HabitSettings = () => {
  const [habitData, setHabitData] = useState(null);
  const [weeklyGoals, setWeeklyGoals] = useState({
    studyDays: 5,
    studyHours: 20,
    subjects: 3
  });
  const [notifications, setNotifications] = useState({
    enabled: true,
    reminderTime: '18:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    loadHabitData();
  }, []);

  const loadHabitData = () => {
    const data = habitService.getHabitData();
    setHabitData(data);
    setWeeklyGoals(data.weeklyGoals);
    setNotifications(data.notifications);
  };

  const handleGoalChange = (field, value) => {
    setWeeklyGoals(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (field, value) => {
    setNotifications(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayToggle = (day) => {
    setNotifications(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      habitService.setWeeklyGoals(weeklyGoals);
      habitService.setNotificationPreferences(notifications);
      
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } catch (error) {
      console.error('Error saving habit settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const getGoalProgress = (goalType) => {
    if (!habitData) return 0;
    
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const weekSessions = habitData.studyHistory.filter(session => {
      const sessionDate = new Date(session.date);
      return sessionDate >= weekStart && sessionDate <= weekEnd;
    });

    switch (goalType) {
      case 'studyDays':
        return Math.min((weekSessions.length / weeklyGoals.studyDays) * 100, 100);
      case 'studyHours':
        const totalHours = weekSessions.reduce((sum, session) => sum + session.duration, 0);
        return Math.min((totalHours / weeklyGoals.studyHours) * 100, 100);
      case 'subjects':
        const uniqueSubjects = new Set();
        weekSessions.forEach(session => {
          session.subjects.forEach(subject => uniqueSubjects.add(subject));
        });
        return Math.min((uniqueSubjects.size / weeklyGoals.subjects) * 100, 100);
      default:
        return 0;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-blue-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <SettingsIcon className="w-6 h-6" />
              Habit Settings
            </h3>
            <p className="text-green-100 mt-1">
              Customize your study goals and reminders
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Weekly Goals Section */}
        <div className="space-y-6">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <ScheduleIcon className="w-5 h-5 text-blue-500" />
            Weekly Study Goals
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Study Days Goal */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Study Days
                </label>
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  {weeklyGoals.studyDays} days
                </span>
              </div>
              
              <input
                type="range"
                min="1"
                max="7"
                value={weeklyGoals.studyDays}
                onChange={(e) => handleGoalChange('studyDays', parseInt(e.target.value))}
                className="w-full h-2 bg-blue-200 dark:bg-blue-700 rounded-lg appearance-none cursor-pointer"
              />
              
              <div className="mt-3">
                <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mb-1">
                  <span>Progress</span>
                  <span>{Math.round(getGoalProgress('studyDays'))}%</span>
                </div>
                <div className="w-full bg-blue-200 dark:bg-blue-700 rounded-full h-2">
                  <motion.div 
                    className="bg-blue-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${getGoalProgress('studyDays')}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            </div>

            {/* Study Hours Goal */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-green-800 dark:text-green-200">
                  Study Hours
                </label>
                <span className="text-sm text-green-600 dark:text-green-400">
                  {weeklyGoals.studyHours}h
                </span>
              </div>
              
              <input
                type="range"
                min="5"
                max="40"
                step="5"
                value={weeklyGoals.studyHours}
                onChange={(e) => handleGoalChange('studyHours', parseInt(e.target.value))}
                className="w-full h-2 bg-green-200 dark:bg-green-700 rounded-lg appearance-none cursor-pointer"
              />
              
              <div className="mt-3">
                <div className="flex justify-between text-xs text-green-600 dark:text-green-400 mb-1">
                  <span>Progress</span>
                  <span>{Math.round(getGoalProgress('studyHours'))}%</span>
                </div>
                <div className="w-full bg-green-200 dark:bg-green-700 rounded-full h-2">
                  <motion.div 
                    className="bg-green-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${getGoalProgress('studyHours')}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            </div>

            {/* Subjects Goal */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-purple-800 dark:text-purple-200">
                  Subjects
                </label>
                <span className="text-sm text-purple-600 dark:text-purple-400">
                  {weeklyGoals.subjects} subjects
                </span>
              </div>
              
              <input
                type="range"
                min="1"
                max="6"
                value={weeklyGoals.subjects}
                onChange={(e) => handleGoalChange('subjects', parseInt(e.target.value))}
                className="w-full h-2 bg-purple-200 dark:bg-purple-700 rounded-lg appearance-none cursor-pointer"
              />
              
              <div className="mt-3">
                <div className="flex justify-between text-xs text-purple-600 dark:text-purple-400 mb-1">
                  <span>Progress</span>
                  <span>{Math.round(getGoalProgress('subjects'))}%</span>
                </div>
                <div className="w-full bg-purple-200 dark:bg-purple-700 rounded-full h-2">
                  <motion.div 
                    className="bg-purple-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${getGoalProgress('subjects')}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="space-y-6">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <NotificationsIcon className="w-5 h-5 text-orange-500" />
            Notification Preferences
          </h4>

          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-6">
            {/* Enable Notifications */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h5 className="font-medium text-orange-800 dark:text-orange-200">
                  Enable Study Reminders
                </h5>
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  Get notified about your daily study sessions
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.enabled}
                  onChange={(e) => handleNotificationChange('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-orange-200 dark:bg-orange-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>

            {/* Reminder Time */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                Reminder Time
              </label>
              <input
                type="time"
                value={notifications.reminderTime}
                onChange={(e) => handleNotificationChange('reminderTime', e.target.value)}
                className="px-3 py-2 border border-orange-300 dark:border-orange-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Days of Week */}
            <div>
              <label className="block text-sm font-medium text-orange-800 dark:text-orange-200 mb-3">
                Reminder Days
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {daysOfWeek.map((day) => (
                  <label
                    key={day.key}
                    className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-800/30 transition-colors duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={notifications.days.includes(day.key)}
                      onChange={() => handleDayToggle(day.key)}
                      className="w-4 h-4 text-orange-500 bg-orange-100 border-orange-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-orange-700 dark:text-orange-300">
                      {day.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Current Stats */}
        {habitData && (
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <BookIcon className="w-5 h-5 text-indigo-500" />
              Current Stats
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {habitData.currentStreak}
                </div>
                <div className="text-sm text-indigo-600 dark:text-indigo-400">
                  Day Streak
                </div>
              </div>

              <div className="bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                  {habitData.totalStudyDays}
                </div>
                <div className="text-sm text-pink-600 dark:text-pink-400">
                  Total Days
                </div>
              </div>

              <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                  {habitData.longestStreak}
                </div>
                <div className="text-sm text-teal-600 dark:text-teal-400">
                  Longest Streak
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <motion.button
            onClick={handleSave}
            disabled={isSaving}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              isSaving
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-blue-600 text-white hover:from-green-600 hover:to-blue-700'
            }`}
            whileHover={!isSaving ? { scale: 1.05 } : {}}
            whileTap={!isSaving ? { scale: 0.95 } : {}}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : showSaved ? (
              <>
                <CheckIcon className="w-4 h-4" />
                Saved!
              </>
            ) : (
              <>
                <SaveIcon className="w-4 h-4" />
                Save Settings
              </>
            )}
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default HabitSettings; 